/**
 * 业绩数据录入弹窗
 */

import React, { useState, useEffect } from 'react';
import { Modal, Form, Select, InputNumber, Divider, message, Spin, Row, Col } from 'antd';
import {
    departmentAPI, Department,
} from '../../../services/organizationAPI';
import {
    PerformancePeriod,
    KPIDimension,
    performancePeriodService,
    kpiDimensionService,
    performanceRecordService,
    kpiConfigService
} from '../../../services/administrationService';

interface PerformanceEntryModalProps {
    visible: boolean;
    onCancel: () => void;
    onSuccess: () => void;
}

const PerformanceEntryModal: React.FC<PerformanceEntryModalProps> = ({ visible, onCancel, onSuccess }) => {
    const [departments, setDepartments] = useState<Department[]>([]);
    const [periods, setPeriods] = useState<PerformancePeriod[]>([]);
    const [dimensions, setDimensions] = useState<KPIDimension[]>([]);
    const [targets, setTargets] = useState<Record<number, number>>({});

    // 选中的状态
    const [selectedDept, setSelectedDept] = useState<number>();
    const [selectedPeriod, setSelectedPeriod] = useState<number>();

    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();

    // 初始化加载
    useEffect(() => {
        if (visible) {
            loadInitData();
        }
    }, [visible]);

    const loadInitData = async () => {
        try {
            const [deptRes, periodRes, dimRes] = await Promise.all([
                departmentAPI.list(),
                performancePeriodService.list({ status: 'active' }),
                kpiDimensionService.list({ is_active: true })
            ]);

            // 确保 data 是数组
            setDepartments(Array.isArray(deptRes.data) ? deptRes.data : (deptRes.data as any).results || []);
            setPeriods(periodRes);
            setDimensions(dimRes);
        } catch (error) {
            message.error('加载初始化数据失败');
        }
    };

    // 当部门和周期都选定后，加载现有的记录和目标值
    useEffect(() => {
        if (selectedDept && selectedPeriod) {
            loadRecordsAndConfigs();
        }
    }, [selectedDept, selectedPeriod]);

    const loadRecordsAndConfigs = async () => {
        if (!selectedDept || !selectedPeriod) return;
        setLoading(true);
        try {
            // 配置已简化，不再包含 target_value
            // 目标值现在在 MemberKPITarget 中设置
            setTargets({});

            // 重置表单值
            form.resetFields(dimensions.map(d => `dim_${d.id}`));

        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!selectedDept || !selectedPeriod) {
            message.warning('请选择部门和考核周期');
            return;
        }

        try {
            const values = await form.validateFields();
            const records = dimensions
                .map(dim => {
                    const val = values[`dim_${dim.id}`];
                    if (val === undefined || val === null) return null;
                    return {
                        department_id: selectedDept,
                        dimension_id: dim.id, // 这里后端需要 dimension_id 而不是 dimension
                        actual_value: val,
                        period_id: selectedPeriod
                        // 备注暂略
                    };
                })
                .filter(Boolean); // 过滤掉未填写的

            if (records.length === 0) {
                message.warning('请至少填写一项数据');
                return;
            }

            // @ts-ignore
            await performanceRecordService.batchCreate(selectedPeriod, records);
            message.success('录入成功');
            onSuccess();
        } catch (error) {
            console.error(error);
            message.error('保存失败');
        }
    };

    return (
        <Modal
            title="KPI业绩录入"
            open={visible}
            onCancel={onCancel}
            onOk={handleSave}
            width={700}
            confirmLoading={loading}
        >
            <Form form={form} layout="vertical">
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item label="考核周期" required>
                            <Select
                                value={selectedPeriod}
                                onChange={setSelectedPeriod}
                                placeholder="选择周期"
                            >
                                {periods.map(p => (
                                    <Select.Option key={p.id} value={p.id}>{p.name}</Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label="部门" required>
                            <Select
                                value={selectedDept}
                                onChange={setSelectedDept}
                                placeholder="选择部门"
                            >
                                {departments.map(d => (
                                    <Select.Option key={d.id} value={d.id}>{d.name}</Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>

                <Divider orientation="left">业绩数据</Divider>

                {selectedDept && selectedPeriod ? (
                    <Spin spinning={loading}>
                        <div style={{ maxHeight: 400, overflowY: 'auto', paddingRight: 8 }}>
                            {dimensions.map(dim => (
                                <Form.Item
                                    key={dim.id}
                                    label={
                                        <span>
                                            {dim.name}
                                            {targets[dim.id] !== undefined && (
                                                <span style={{ color: '#888', marginLeft: 8, fontSize: 12 }}>
                                                    Target: {targets[dim.id]}
                                                </span>
                                            )}
                                        </span>
                                    }
                                    name={`dim_${dim.id}`}
                                >
                                    <InputNumber style={{ width: '100%' }} placeholder="输入实际完成值" />
                                </Form.Item>
                            ))}
                        </div>
                    </Spin>
                ) : (
                    <div style={{ textAlign: 'center', color: '#999', padding: '20px 0' }}>
                        请先选择周期和部门
                    </div>
                )}
            </Form>
        </Modal>
    );
};

export default PerformanceEntryModal;
